import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import BottomNav from '../components/BottomNav';

interface TableRow {
  id: string;
  table_number: number;
  section: string | null;
  nfc_uid: string | null;
  qr_token: string | null;
}

const PAY_BASE = (import.meta.env.VITE_TAPNPAY_PAY_BASE_URL as string | undefined) ??
  'https://pay.tapnpay.ma';

function buildQrUrl(t: TableRow): string {
  if (t.qr_token) return `${PAY_BASE}/t/${t.qr_token}`;
  if (t.nfc_uid) return `${PAY_BASE}/t/${t.nfc_uid}`;
  return `${PAY_BASE}/table/${t.id}`;
}

function qrImageSrc(data: string, size = 280): string {
  // Browser-friendly external QR generator — no extra npm dependency.
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&qzone=2&data=${encodeURIComponent(
    data,
  )}`;
  return url;
}

export default function QrCodesPage() {
  const { session } = useAuth();
  const [tables, setTables] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.storeId) return;
    (async () => {
      const { data } = await supabase
        .from('restaurant_tables')
        .select('id, table_number, section, nfc_uid, qr_token')
        .eq('store_id', session.storeId)
        .order('table_number');
      setTables((data as TableRow[] | null) ?? []);
      setLoading(false);
    })();
  }, [session?.storeId]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12 print:hidden"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
            QR codes des tables
          </p>
          <p className="text-[10px] text-on-surface-variant">
            Imprimez ces QR codes pour vos tables
          </p>
        </div>
        <button
          type="button"
          onClick={handlePrint}
          className="min-h-[36px] px-3 text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary inline-flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[16px]">print</span>
          Imprimer
        </button>
      </header>

      {loading ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">…</div>
      ) : tables.length === 0 ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">
          Aucune table configurée
        </div>
      ) : (
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 print:grid-cols-2">
          {tables.map((t) => {
            const url = buildQrUrl(t);
            return (
              <div
                key={t.id}
                className="bg-surface p-4 flex flex-col items-center gap-2 print:break-inside-avoid"
                style={{ border: '0.5px solid #000' }}
              >
                <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
                  Table {t.table_number}
                </p>
                {t.section && (
                  <p className="text-[10px] text-on-surface-variant">{t.section}</p>
                )}
                <img
                  src={qrImageSrc(url)}
                  alt={`QR Table ${t.table_number}`}
                  width={280}
                  height={280}
                  className="my-2"
                />
                <p className="text-[10px] text-on-surface-variant break-all text-center">
                  {url}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <div className="print:hidden">
        <BottomNav />
      </div>
    </div>
  );
}
